import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  DonateGoalTheme,
  GRADIENT_PRESET_BG_TO,
  darkenHex,
  hexLuminance,
  hexToRgba,
  resolveDonateGoalTheme,
} from './donate-goal-theme';

/**
 * Thẻ mục tiêu donate dùng chung cho overlay OBS, trang creator, ô xem trước và các ô mẫu trong
 * cài đặt. Mọi nơi vẽ cùng một component nên streamer thấy gì khi chỉnh thì trên live hiện y vậy.
 *
 * Hiệu ứng chạy hoàn toàn bằng CSS (transform / background-position) để OBS không tốn CPU; số tiền
 * đếm lên bằng requestAnimationFrame. Người dùng bật "giảm chuyển động" thì tắt hết phần động.
 */

export type DonateGoalWidgetSize = 'overlay' | 'card' | 'mini';

export interface DonateGoalWidgetProps {
  title: string;
  description?: string | null;
  currentAmount: number;
  targetAmount: number;
  donationCount?: number;
  isExpired?: boolean;
  isActive?: boolean;
  theme?: DonateGoalTheme | null;
  size?: DonateGoalWidgetSize;
  /** Tăng lên mỗi khi có donate mới: thẻ nảy nhẹ và thanh lóe sáng. */
  animateKey?: number;
  /** Hiện nhãn "Đã hết hạn" / "Đang tắt" (trang creator, xem trước trong cài đặt). */
  showStatus?: boolean;
  className?: string;
}

const STYLE_ID = 'cab-donate-goal-styles';

const CSS = `
@keyframes cabdg-stripes{from{background-position:0 0}to{background-position:28px 0}}
@keyframes cabdg-sweep{0%{transform:translateX(-130%)}55%,100%{transform:translateX(330%)}}
@keyframes cabdg-flash{0%{opacity:0}20%{opacity:.85}100%{opacity:0}}
@keyframes cabdg-bump-a{0%{transform:scale(1)}30%{transform:scale(1.035)}62%{transform:scale(.996)}100%{transform:scale(1)}}
@keyframes cabdg-bump-b{0%{transform:scale(1)}30%{transform:scale(1.035)}62%{transform:scale(.996)}100%{transform:scale(1)}}
@keyframes cabdg-edge{0%,100%{opacity:.7;transform:translate(-50%,-50%) scale(.9)}50%{opacity:1;transform:translate(-50%,-50%) scale(1.15)}}
@keyframes cabdg-twinkle{0%,100%{opacity:.55;transform:scale(.75) rotate(0deg)}50%{opacity:1;transform:scale(1.1) rotate(25deg)}}
@keyframes cabdg-gold{0%,100%{box-shadow:0 0 0 0 rgba(251,191,36,0)}50%{box-shadow:0 0 14px 1px rgba(251,191,36,.45)}}
.cabdg-stripes{position:absolute;inset:0;border-radius:inherit;background-image:linear-gradient(135deg,var(--cabdg-stripe) 25%,transparent 25%,transparent 50%,var(--cabdg-stripe) 50%,var(--cabdg-stripe) 75%,transparent 75%,transparent);background-size:28px 28px;animation:cabdg-stripes 1.1s linear infinite;pointer-events:none}
.cabdg-sweep{position:absolute;top:0;bottom:0;left:0;width:35%;background:linear-gradient(90deg,rgba(255,255,255,0),rgba(255,255,255,.5),rgba(255,255,255,0));animation:cabdg-sweep 2.8s ease-in-out infinite;will-change:transform;pointer-events:none}
.cabdg-flash{position:absolute;inset:0;border-radius:inherit;background:#fff;opacity:0;animation:cabdg-flash .8s ease-out 1;pointer-events:none}
.cabdg-bump-a{animation:cabdg-bump-a .7s cubic-bezier(.2,.8,.2,1) 1}
.cabdg-bump-b{animation:cabdg-bump-b .7s cubic-bezier(.2,.8,.2,1) 1}
.cabdg-edge{animation:cabdg-edge 1.8s ease-in-out infinite}
.cabdg-twinkle{animation:cabdg-twinkle 1.6s ease-in-out infinite}
.cabdg-gold{animation:cabdg-gold 2.4s ease-in-out infinite}
.cabdg-fill,.cabdg-edge-pos{transition:width 1.2s cubic-bezier(.22,1,.36,1),left 1.2s cubic-bezier(.22,1,.36,1),opacity .3s ease,background .6s ease}
@media (prefers-reduced-motion: reduce){
  .cabdg-stripes,.cabdg-sweep,.cabdg-flash,.cabdg-bump-a,.cabdg-bump-b,.cabdg-edge,.cabdg-twinkle,.cabdg-gold{animation:none!important}
  .cabdg-sweep,.cabdg-flash{display:none}
  .cabdg-fill,.cabdg-edge-pos{transition:none!important}
}
`;

const ensureStyles = () => {
  if (typeof document === 'undefined' || document.getElementById(STYLE_ID)) return;
  const el = document.createElement('style');
  el.id = STYLE_ID;
  el.textContent = CSS;
  document.head.appendChild(el);
};

const REDUCED_QUERY = '(prefers-reduced-motion: reduce)';

const readReducedMotion = () =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(REDUCED_QUERY).matches;

export const usePrefersReducedMotion = () => {
  const [reduced, setReduced] = useState(readReducedMotion);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return undefined;
    const mq = window.matchMedia(REDUCED_QUERY);
    const onChange = () => setReduced(mq.matches);
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else mq.removeListener(onChange);
    };
  }, []);
  return reduced;
};

/** Đếm số từ giá trị đang hiện tới giá trị mới (~1 giây, chậm dần về cuối). */
const useCountUp = (value: number, enabled: boolean, startFromZero: boolean) => {
  const [display, setDisplay] = useState(enabled && startFromZero ? 0 : value);
  const displayRef = useRef(display);

  useEffect(() => {
    if (!enabled || typeof window.requestAnimationFrame !== 'function') {
      displayRef.current = value;
      setDisplay(value);
      return undefined;
    }
    const from = displayRef.current;
    if (from === value) return undefined;
    const start = performance.now();
    const duration = 1100;
    let raf = 0;
    const tick = (now: number) => {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      const next = t >= 1 ? value : from + (value - from) * eased;
      displayRef.current = next;
      setDisplay(next);
      if (t < 1) raf = window.requestAnimationFrame(tick);
    };
    raf = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(raf);
  }, [value, enabled]);

  return Math.round(display);
};

interface SizeSpec {
  padY: number;
  padX: number;
  radius: number;
  title: number;
  count: number;
  amount: number;
  unit: number;
  pct: number;
  target: number;
  bar: number;
  desc: number;
  badge: number;
  gapTop: number;
  gapBar: number;
  gapBottom: number;
}

const SIZES: Record<DonateGoalWidgetSize, SizeSpec> = {
  overlay: {
    padY: 18,
    padX: 22,
    radius: 20,
    title: 20,
    count: 15,
    amount: 30,
    unit: 15,
    pct: 20,
    target: 16,
    bar: 14,
    desc: 15,
    badge: 13,
    gapTop: 8,
    gapBar: 12,
    gapBottom: 10,
  },
  card: {
    padY: 14,
    padX: 16,
    radius: 16,
    title: 16,
    count: 13,
    amount: 22,
    unit: 12,
    pct: 15,
    target: 13,
    bar: 10,
    desc: 13,
    badge: 11,
    gapTop: 6,
    gapBar: 10,
    gapBottom: 8,
  },
  mini: {
    padY: 9,
    padX: 10,
    radius: 11,
    title: 11,
    count: 9,
    amount: 13,
    unit: 0,
    pct: 11,
    target: 9,
    bar: 6,
    desc: 9,
    badge: 8,
    gapTop: 4,
    gapBar: 6,
    gapBottom: 5,
  },
};

// Nền trong suốt nằm thẳng trên video: chữ cần bóng đổ nhiều lớp mới tách khỏi mọi loại nền.
const OUTLINE = '0 0 3px rgba(0,0,0,.95), 0 2px 6px rgba(0,0,0,.85), 0 0 14px rgba(0,0,0,.6)';
const OUTLINE_MINI = '0 0 2px rgba(0,0,0,.95), 0 1px 3px rgba(0,0,0,.8)';

const GOLD_FILL = 'linear-gradient(90deg,#F59E0B 0%,#FBBF24 55%,#FDE047 100%)';

const fmt = (n: number) => Math.max(0, Math.round(n || 0)).toLocaleString('vi-VN');

const Sparkle: React.FC<{ size: number; color: string; animated: boolean }> = ({
  size,
  color,
  animated,
}) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    aria-hidden="true"
    className={animated ? 'cabdg-twinkle' : undefined}
    style={{ flexShrink: 0 }}
  >
    <path
      d="M12 1.5c.6 4.6 2.9 7.9 10.5 10.5-7.6 2.6-9.9 5.9-10.5 10.5C11.4 17.9 9.1 14.6 1.5 12 9.1 9.4 11.4 6.1 12 1.5z"
      fill={color}
    />
  </svg>
);

const DonateGoalWidget: React.FC<DonateGoalWidgetProps> = ({
  title,
  description,
  currentAmount,
  targetAmount,
  donationCount = 0,
  isExpired = false,
  isActive = true,
  theme,
  size = 'card',
  animateKey = 0,
  showStatus = false,
  className,
}) => {
  useLayoutEffect(ensureStyles, []);

  const t = resolveDonateGoalTheme(theme);
  const s = SIZES[size];
  const reducedMotion = usePrefersReducedMotion();
  const motion = !reducedMotion;
  const isMini = size === 'mini';

  const current = Math.max(0, Number(currentAmount) || 0);
  const target = Math.max(0, Number(targetAmount) || 0);
  const reached = target > 0 && current >= target;
  const percent = target > 0 ? Math.min(100, Math.floor((current * 100) / target)) : 0;

  // Số đếm lên và % đi theo số đang hiện để hai con số luôn khớp nhau trong lúc chạy.
  const shownAmount = useCountUp(current, motion && !isMini, true);
  const shownPct = reached && shownAmount >= current
    ? 100
    : target > 0
      ? Math.min(100, Math.floor((shownAmount * 100) / target))
      : 0;

  // Thanh bắt đầu từ 0 rồi chạy tới vị trí thật ngay sau lần vẽ đầu (hiệu ứng "đang nạp").
  const [barPct, setBarPct] = useState(motion && !isMini ? 0 : percent);
  useEffect(() => {
    if (!motion || isMini || typeof window.requestAnimationFrame !== 'function') {
      setBarPct(percent);
      return undefined;
    }
    const raf = window.requestAnimationFrame(() => setBarPct(percent));
    return () => window.cancelAnimationFrame(raf);
  }, [percent, motion, isMini]);

  const alpha = t.bgOpacity / 100;
  const bgIsLight = hexLuminance(t.bg) > 0.5;
  const surfaceLight = alpha >= 0.35 && bgIsLight;
  const isNeon = t.preset === 'neon';
  const noSurface = alpha === 0;

  let background: string;
  if (noSurface) {
    background = 'transparent';
  } else if (t.preset === 'gradient') {
    const to = t.bg === '#0057FF' ? GRADIENT_PRESET_BG_TO : darkenHex(t.bg, 0.4);
    background = `radial-gradient(120% 90% at 0% 0%, rgba(255,255,255,${0.2 * alpha}) 0%, rgba(255,255,255,0) 55%), linear-gradient(135deg, ${hexToRgba(
      t.bg,
      alpha
    )} 0%, ${hexToRgba(to, alpha)} 100%)`;
  } else {
    background = hexToRgba(t.bg, alpha);
  }

  const shadowScale = isMini ? 0.4 : 1;
  let border = 'none';
  let boxShadow = 'none';
  if (!noSurface) {
    if (isNeon) {
      border = `1px solid ${hexToRgba(t.barFrom, 0.45)}`;
      boxShadow = `0 0 0 1px ${hexToRgba(t.barTo, 0.12)}, 0 0 ${28 * shadowScale}px ${hexToRgba(
        t.barFrom,
        0.22
      )}, 0 ${12 * shadowScale}px ${32 * shadowScale}px rgba(0,0,0,.45)`;
    } else if (surfaceLight) {
      border = '1px solid rgba(15,23,42,.08)';
      boxShadow = `0 ${12 * shadowScale}px ${32 * shadowScale}px rgba(15,23,42,.18), 0 2px 6px rgba(15,23,42,.08)`;
    } else {
      border = `1px solid rgba(255,255,255,${t.preset === 'gradient' ? 0.2 : 0.12})`;
      boxShadow = `0 ${12 * shadowScale}px ${32 * shadowScale}px rgba(0,0,0,.35), inset 0 1px 0 rgba(255,255,255,.08)`;
    }
  }

  const textShadow =
    alpha < 0.35
      ? isMini
        ? OUTLINE_MINI
        : OUTLINE
      : isNeon
        ? `0 0 ${isMini ? 6 : 12}px ${hexToRgba(t.barFrom, 0.55)}`
        : surfaceLight
          ? 'none'
          : '0 1px 2px rgba(0,0,0,.25)';

  const muted = hexToRgba(t.text, 0.66);
  const textIsLight = hexLuminance(t.text) > 0.5;
  const trackBg =
    alpha < 0.35 ? 'rgba(0,0,0,.45)' : hexToRgba(t.text, textIsLight ? 0.16 : 0.1);

  const fillBg = reached ? GOLD_FILL : `linear-gradient(90deg, ${t.barFrom} 0%, ${t.barTo} 100%)`;
  const edgeColor = reached ? '#FDE047' : t.barTo;
  const fillGlow = reached
    ? `0 0 ${isMini ? 6 : 12}px rgba(251,191,36,.6)`
    : isNeon
      ? `0 0 ${isMini ? 4 : 8}px ${hexToRgba(t.barFrom, 0.9)}, 0 0 ${isMini ? 9 : 18}px ${hexToRgba(
          t.barTo,
          0.6
        )}`
      : `0 0 ${isMini ? 5 : 10}px ${hexToRgba(t.barTo, 0.35)}`;
  const stripeColor =
    hexLuminance(reached ? '#FBBF24' : t.barFrom) > 0.6
      ? 'rgba(15,23,42,.10)'
      : 'rgba(255,255,255,.18)';

  const goldText = surfaceLight ? '#B45309' : '#FDE68A';
  const bumpClass = animateKey > 0 && motion ? (animateKey % 2 ? 'cabdg-bump-a' : 'cabdg-bump-b') : '';
  const count = Math.max(0, donationCount || 0);
  const showExpired = showStatus && isExpired;
  const showInactive = showStatus && !isActive;
  const hasFooter = reached || showExpired || showInactive || !!description;

  const pill = (label: string) => (
    <span
      key={label}
      className="shrink-0 rounded-full"
      style={{
        fontSize: s.badge,
        lineHeight: 1.2,
        padding: isMini ? '1px 6px' : '2px 8px',
        fontWeight: 600,
        color: t.text,
        background: hexToRgba(t.text, 0.12),
        border: `1px solid ${hexToRgba(t.text, 0.18)}`,
      }}
    >
      {label}
    </span>
  );

  return (
    <div
      className={`relative w-full ${bumpClass} ${className || ''}`}
      data-testid="donate-goal-widget"
      data-preset={t.preset}
      data-text={t.text}
      style={{
        padding: `${s.padY}px ${s.padX}px`,
        borderRadius: s.radius,
        background,
        border,
        boxShadow,
        backdropFilter: !noSurface && alpha < 1 ? 'blur(14px) saturate(140%)' : undefined,
        WebkitBackdropFilter: !noSurface && alpha < 1 ? 'blur(14px) saturate(140%)' : undefined,
        color: t.text,
        textShadow,
        fontVariantNumeric: 'tabular-nums',
        transformOrigin: 'center',
      }}
    >
      {/* Hàng 1: tên mục tiêu + số lượt */}
      <div className="flex items-baseline justify-between" style={{ gap: s.padX / 2 }}>
        <span
          className="min-w-0 truncate"
          style={{ fontSize: s.title, fontWeight: 700, letterSpacing: '-0.01em', lineHeight: 1.25 }}
        >
          {title}
        </span>
        {count > 0 && (
          <span
            className="shrink-0"
            style={{ fontSize: s.count, fontWeight: 500, color: muted, lineHeight: 1.25 }}
          >
            {fmt(count)} lượt
          </span>
        )}
      </div>

      {/* Hàng 2: đã nhận · % · mục tiêu */}
      <div
        className="grid items-baseline"
        style={{
          gridTemplateColumns: '1fr auto 1fr',
          columnGap: s.padX / 2,
          marginTop: s.gapTop,
          lineHeight: 1.1,
        }}
      >
        <span className="min-w-0 truncate" data-testid="donate-goal-amount">
          <span
            style={{
              fontSize: s.amount,
              fontWeight: 800,
              letterSpacing: '-0.02em',
              color: t.accent,
              textShadow:
                isNeon && !noSurface ? `0 0 ${isMini ? 6 : 12}px ${hexToRgba(t.accent, 0.6)}` : undefined,
            }}
          >
            {fmt(shownAmount)}
          </span>
          {s.unit > 0 && (
            <span style={{ fontSize: s.unit, fontWeight: 600, color: muted, marginLeft: 4 }}>
              Ca
            </span>
          )}
        </span>
        <span
          data-testid="donate-goal-percent"
          style={{ fontSize: s.pct, fontWeight: 800, color: reached ? goldText : t.text }}
        >
          {shownPct}%
        </span>
        <span
          className="min-w-0 truncate text-right"
          style={{ fontSize: s.target, fontWeight: 600, color: muted }}
        >
          {fmt(target)} Ca
        </span>
      </div>

      {/* Hàng 3: thanh tiến độ */}
      <div
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={percent}
        aria-label="Tiến độ mục tiêu donate"
        className="relative w-full rounded-full"
        style={{
          height: s.bar,
          marginTop: s.gapBar,
          background: trackBg,
          boxShadow: `inset 0 1px 2px rgba(0,0,0,${surfaceLight ? 0.12 : 0.3})`,
        }}
      >
        <div
          className="cabdg-fill relative h-full overflow-hidden rounded-full"
          data-testid="donate-goal-fill"
          data-bar-from={reached ? '#F59E0B' : t.barFrom}
          data-bar-to={reached ? '#FDE047' : t.barTo}
          style={
            {
              width: `${barPct}%`,
              minWidth: barPct > 0 ? s.bar : 0,
              background: fillBg,
              boxShadow: `${fillGlow}, inset 0 1px 0 rgba(255,255,255,.35)`,
              '--cabdg-stripe': stripeColor,
            } as React.CSSProperties
          }
        >
          {motion && barPct > 0 && <span className="cabdg-stripes" />}
          {motion && barPct > 0 && <span className="cabdg-sweep" />}
          {animateKey > 0 && motion && <span key={animateKey} className="cabdg-flash" />}
        </div>
        {/* Luôn gắn sẵn (kể cả ở 0%) để đốm sáng chạy cùng nhịp với thanh ngay lần nạp đầu. */}
        {current > 0 && (
          <span
            aria-hidden="true"
            className="cabdg-edge-pos pointer-events-none absolute"
            style={{
              left: `${barPct}%`,
              top: '50%',
              width: 0,
              height: 0,
              opacity: barPct > 0 ? 1 : 0,
            }}
          >
            <span
              className={`absolute rounded-full ${motion ? 'cabdg-edge' : ''}`}
              style={{
                left: 0,
                top: 0,
                width: s.bar * 2.4,
                height: s.bar * 2.4,
                transform: 'translate(-50%,-50%)',
                background: `radial-gradient(circle, rgba(255,255,255,.95) 0%, ${hexToRgba(
                  edgeColor,
                  0.7
                )} 28%, rgba(255,255,255,0) 66%)`,
                filter: 'blur(0.5px)',
              }}
            />
          </span>
        )}
      </div>

      {/* Hàng 4: đạt mục tiêu / trạng thái / mô tả */}
      {hasFooter && (
        <div className="flex min-w-0 items-center" style={{ gap: s.padX / 2.5, marginTop: s.gapBottom }}>
          {reached && (
            <span
              className={`inline-flex shrink-0 items-center rounded-full ${motion ? 'cabdg-gold' : ''}`}
              style={{
                gap: isMini ? 3 : 5,
                fontSize: s.badge,
                lineHeight: 1.2,
                padding: isMini ? '1px 6px' : '3px 10px',
                fontWeight: 700,
                color: goldText,
                background: 'linear-gradient(90deg, rgba(245,158,11,.22), rgba(253,224,71,.22))',
                border: '1px solid rgba(251,191,36,.6)',
              }}
            >
              <Sparkle size={s.badge + 2} color={reached ? '#FBBF24' : t.accent} animated={motion} />
              Đã đạt mục tiêu
            </span>
          )}
          {showExpired && pill('Đã hết hạn')}
          {showInactive && pill('Đang tắt')}
          {description && (
            <span
              className="min-w-0 truncate"
              style={{ fontSize: s.desc, fontWeight: 500, color: muted, lineHeight: 1.3 }}
            >
              {description}
            </span>
          )}
        </div>
      )}
    </div>
  );
};

export default DonateGoalWidget;
